
import React, { useState, useEffect } from 'react';
import { X, Plus, Briefcase, MapPin, DollarSign, ListChecks, Award, Clock } from 'lucide-react';
import { Job, SkillType, JobStatus } from '../types';

interface JobFormProps {
  onSubmit: (job: Omit<Job, 'id' | 'companyId' | 'companyName' | 'applicants'>) => void;
  onCancel: () => void;
  initialData?: Job;
}

export const JobForm: React.FC<JobFormProps> = ({ onSubmit, onCancel, initialData }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    location: '',
    payRate: '',
    requiredSkills: [] as SkillType[],
    requiredCerts: [] as string[],
    status: JobStatus.OPEN,
    deadline: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title,
        description: initialData.description,
        date: initialData.date,
        location: initialData.location,
        payRate: initialData.payRate,
        requiredSkills: initialData.requiredSkills,
        requiredCerts: initialData.requiredCerts,
        status: initialData.status,
        deadline: initialData.deadline || ''
      });
    }
  }, [initialData]);

  const [newCert, setNewCert] = useState('');

  const toggleSkill = (skill: SkillType) => {
    setFormData(prev => ({
      ...prev,
      requiredSkills: prev.requiredSkills.includes(skill)
        ? prev.requiredSkills.filter(s => s !== skill)
        : [...prev.requiredSkills, skill]
    }));
  };

  const addCert = () => {
    if (newCert.trim()) {
      setFormData(prev => ({
        ...prev,
        requiredCerts: [...prev.requiredCerts, newCert.trim()]
      }));
      setNewCert('');
    }
  };

  const removeCert = (certToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      requiredCerts: prev.requiredCerts.filter(c => c !== certToRemove)
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="sticky top-0 bg-white border-b border-slate-100 p-6 flex justify-between items-center z-10">
          <div>
            <h2 className="text-2xl font-black text-slate-800 tracking-tight">
              {initialData ? 'Edit Job Posting' : 'Post a New Job'}
            </h2>
            <p className="text-sm text-slate-500">
              {initialData ? 'Update the details for this project' : 'Find the right pros for your project'}
            </p>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
            <X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1 uppercase tracking-wider">Job Title</label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  required
                  type="text"
                  placeholder="e.g. 80ft Crane Removal"
                  className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1 uppercase tracking-wider">Job Date</label>
                <div className="flex gap-2">
                  <input
                    required
                    type="date"
                    className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none"
                    value={formData.date}
                    onChange={e => setFormData({ ...formData, date: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      const response = await fetch('/api/ai/suggest-dates', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ jobId: 'new' })
                      });
                      const suggestion = await response.json();
                      if (suggestion.suggestedSlots && suggestion.suggestedSlots.length > 0) {
                        alert(`Suggested Dates:\n${suggestion.suggestedSlots.map((s: any) => `${s.start} to ${s.end}`).join('\n')}\n\nReasoning: ${suggestion.reasoning}`);
                      } else {
                        alert('No available dates found.');
                      }
                    }}
                    className="px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold text-sm hover:bg-emerald-100 hover:text-emerald-700 transition"
                  >
                    Suggest
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1 uppercase tracking-wider">Application Deadline</label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="date"
                    className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none"
                    value={formData.deadline}
                    onChange={e => setFormData({ ...formData, deadline: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1 uppercase tracking-wider">Location</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  required
                  type="text"
                  placeholder="City, State"
                  className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none"
                  value={formData.location}
                  onChange={e => setFormData({ ...formData, location: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1 uppercase tracking-wider">Pay Rate</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  required
                  type="text"
                  placeholder="e.g. $45/hr or $500 total"
                  className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none"
                  value={formData.payRate}
                  onChange={e => setFormData({ ...formData, payRate: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1 uppercase tracking-wider">Description</label>
              <textarea
                required
                rows={3}
                placeholder="Detail the scope of work, hazards, and equipment needed..."
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none resize-none"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider flex items-center">
                <ListChecks className="w-4 h-4 mr-2 text-emerald-600" /> Required Skills
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.values(SkillType).map(skill => (
                  <button
                    key={skill}
                    type="button"
                    onClick={() => toggleSkill(skill)}
                    className={`px-3 py-2 text-xs font-bold rounded-lg border transition-all text-left flex items-center ${
                      formData.requiredSkills.includes(skill)
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-100'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'
                    }`}
                  >
                    <div className={`w-3 h-3 rounded-full mr-2 border border-current ${formData.requiredSkills.includes(skill) ? 'bg-white' : ''}`} />
                    {skill}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider flex items-center">
                <Award className="w-4 h-4 mr-2 text-amber-600" /> Certifications
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  placeholder="e.g. ISA Certified, First Aid"
                  className="flex-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                  value={newCert}
                  onChange={e => setNewCert(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addCert())}
                />
                <button
                  type="button"
                  onClick={addCert}
                  className="p-2 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-colors"
                >
                  <Plus className="w-6 h-6" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.requiredCerts.map(cert => (
                  <span key={cert} className="inline-flex items-center bg-amber-50 text-amber-700 px-3 py-1.5 rounded-lg border border-amber-100 text-xs font-bold">
                    {cert}
                    <button type="button" onClick={() => removeCert(cert)} className="ml-2 hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-100 flex gap-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-4 text-slate-600 font-bold bg-slate-100 rounded-2xl hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-[2] py-4 bg-emerald-600 text-white font-black text-lg rounded-2xl hover:bg-emerald-700 shadow-xl shadow-emerald-200 transition-all active:scale-95"
            >
              {initialData ? 'Save Changes' : 'Post Job & Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
